import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Award, ChevronLeft, ChevronRight } from "lucide-react";
import { supabase } from "./supabaseClient";
import { BOAT, SHORE, SPECIES_AWARDS, CHAMPION_AWARDS, formatLbs } from "./cfsa-data";
import { calcPct } from "./awardScoring";
import { normalizeName } from "./lib/nameMatching";
import PageMeta from "./PageMeta";
import RayBassLeaderboard from "./RayBassLeaderboard";
import { buildRayBassLeaderboard, huntDateRange, isRayBassHuntActive } from "./rayBassHunt";

const normalizeSpeciesKey = (value) => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\blesser spotted dogfish\b/g, "l.s.d.")
    .replace(/\bl\.?\s*s\.?\s*d\.?\b/g, "l.s.d.")
    .replace(/smooth hound \(st\)#?/g, "smooth hound (st)")
    .replace(/\s+/g, " ")
    .replace(/\(\s*/g, "(")
    .replace(/\s*\)/g, ")")
    .replace(/\s*\/\s*/g, "/")
    .replace(/\s*-\s*/g, "-")
    .replace(/[“”"]/g, "")
    .replace(/[‘’']/g, "")
    .replace(/[^a-z0-9()/. -]+/g, " ")
    .replace(/\s+/g, " ")
    .replace(/[()]/g, "")
    .trim();
  return normalized;
};

const CANONICAL_SPECIES_BY_KEY = Object.fromEntries(
  [...BOAT, ...SHORE].map((record) => [normalizeSpeciesKey(record.species), record.species])
);

function getCanonicalSpeciesName(species) {
  return CANONICAL_SPECIES_BY_KEY[normalizeSpeciesKey(species)] || String(species || "").trim();
}

function formatKgLbsOz(kg) {
  return formatLbs(kg * 2.20462);
}

function formatDate(iso) {
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

function toLocalDateString(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function getMonthYearInfo(month, year) {
  const date = new Date(year, month, 1);
  return {
    month,
    year,
    monthName: date.toLocaleDateString("en-GB", { month: "long" }),
    fullName: date.toLocaleDateString("en-GB", { month: "long", year: "numeric" })
  };
}

const BOAT_AND_SHORE_TROPHIES = [
  {
    name: "Conger Cup",
    description: "Best Specimen Conger",
    type: "heaviest",
    location: ["boat", "shore"],
    speciesPattern: /Conger/i,
  },
  {
    name: "Phil Kevern Cup",
    description: "Best Specimen Greater Spotted Dogfish",
    type: "heaviest",
    location: ["boat", "shore"],
    speciesPattern: /Dogfish|Bull Huss/i,
  },
  {
    name: "Tope Trophy",
    description: "Best Specimen Tope",
    type: "heaviest",
    location: ["boat", "shore"],
    speciesPattern: /Tope/i,
  },
  {
    name: "Shirley-Anne Cup",
    description: "Best aggregate percentage by a Junior Member from 3 different species",
    type: "top3junior",
    location: ["boat", "shore"],
    requiresJunior: true,
  },
  {
    name: "Sandy Underwood Memorial Shield",
    description: "Most species (over CFSA 1 day weights) from boat or shore",
    type: "mostSpeciesOneDay",
    location: ["boat", "shore"],
  },
];

// Junior-only cups and trophies, pulled together from SPECIES_AWARDS and
// BOAT_AND_SHORE_TROPHIES so they can be shown in one dedicated section.
const JUNIOR_TROPHIES = [
  ...SPECIES_AWARDS.filter((a) => a.requiresJunior),
  ...BOAT_AND_SHORE_TROPHIES.filter((a) => a.requiresJunior),
  {
    name: "S.F.B. Trophy",
    description: "Best Specimen by a junior under 13 at beginning of Club year. Awarded manually – club records don't track date of birth against catches.",
  },
];

function matchesAward(catchRow, award) {
  if (award.location) {
    const locations = Array.isArray(award.location) ? award.location : [award.location];
    if (!locations.includes(catchRow.location)) return false;
  }

  if (award.species) {
    const speciesList = Array.isArray(award.species) ? award.species : [award.species];
    const normalizedSpecies = getCanonicalSpeciesName(catchRow.species);
    const normalizedList = speciesList.map(getCanonicalSpeciesName);
    if (!normalizedList.includes(normalizedSpecies)) return false;
  }

  if (award.speciesPattern && !award.speciesPattern.test(catchRow.species)) {
    return false;
  }

  if (award.requiresMethod && award.method === "artificial_lure") {
    if (!catchRow.is_artificial_lure) return false;
  }

  if (award.requiresMethodIncludes && !catchRow.fishing_method) {
    return false;
  }

  if (award.requiresMethodIncludes && catchRow.fishing_method) {
    if (!catchRow.fishing_method.toLowerCase().includes(award.requiresMethodIncludes.toLowerCase())) return false;
  }

  if (award.requiresVenue && award.venue) {
    // Check beach_location first (new field), then fall back to venue for backward compatibility
    const locationToCheck = catchRow.beach_location || catchRow.venue;
    if (!locationToCheck) return false;

    const venues = Array.isArray(award.venue) ? award.venue : [award.venue];
    const matchVenue = award.requiresVenueExact
      ? venues.some((venue) => locationToCheck.toLowerCase() === venue.toLowerCase())
      : venues.some((venue) => locationToCheck.toLowerCase().includes(venue.toLowerCase()));
    if (!matchVenue) return false;
  }

  if (award.requiresVenueIncludes && award.requiresVenueIncludes) {
    // Check beach_location first (new field), then fall back to venue for backward compatibility
    const locationToCheck = catchRow.beach_location || catchRow.venue;
    if (!locationToCheck) return false;

    const includes = Array.isArray(award.requiresVenueIncludes)
      ? award.requiresVenueIncludes
      : [award.requiresVenueIncludes];
    const matchInclude = includes.some((include) => locationToCheck.toLowerCase().includes(include.toLowerCase()));
    if (!matchInclude) return false;
  }

  if (award.requiresBeachLocation) {
    if (!catchRow.beach_location) return false;
    if (catchRow.beach_location !== award.requiresBeachLocation) return false;
  }

  if (award.requiresNonWinner) {
    if (catchRow.is_junior) return false;
    if (catchRow.has_perpetual_trophy || catchRow.has_won_perpetual_trophy) return false;
  }

  if (award.excludesJunior && catchRow.is_junior) {
    return false;
  }

  if (award.requiresLadies && !catchRow.is_female) {
    return false;
  }

  if (award.requiresJunior && !catchRow.is_junior) {
    return false;
  }

  if (award.requiresClubBoatTrip && !catchRow.is_club_trip) {
    return false;
  }

  if (award.excludeSpecies) {
    const excludeList = Array.isArray(award.excludeSpecies) ? award.excludeSpecies : [award.excludeSpecies];
    if (excludeList.map(getCanonicalSpeciesName).includes(getCanonicalSpeciesName(catchRow.species))) return false;
  }

  return true;
}

function calcHeaviestAward(catches, award) {
  const eligible = catches.filter((c) => matchesAward(c, award));
  if (eligible.length === 0) return null;
  return eligible.reduce((best, c) => {
    if (!best || c.weight_kg > best.weight_kg) return c;
    return best;
  }, null);
}

function calcTop3JuniorAward(catches) {
  const juniors = catches.filter((c) => c.is_junior);
  const byAngler = {};
  juniors.forEach((c) => {
    const species = getCanonicalSpeciesName(c.species);
    const pct = calcPct(species, c.location, c.weight_kg);
    if (!pct || pct <= 0) return;
    const key = normalizeName(c.angler_name);
    if (!byAngler[key]) byAngler[key] = { displayName: c.angler_name, species: {} };
    if (!byAngler[key].species[species] || pct > byAngler[key].species[species].pct) {
      byAngler[key].species[species] = { pct, weight_kg: c.weight_kg };
    }
  });

  let best = null;
  Object.values(byAngler).forEach(({ displayName, species: speciesMap }) => {
    const sorted = Object.entries(speciesMap)
      .map(([species, { pct, weight_kg }]) => ({ species, pct, weight_kg }))
      .sort((a, b) => b.pct - a.pct)
      .slice(0, 3);
    if (sorted.length < 3) return;
    const total = sorted.reduce((sum, s) => sum + s.pct, 0);
    if (!best || total > best.total) {
      best = {
        angler_name: displayName,
        total,
        species: sorted.map((s) => s.species),
        metricLabel: "Top 3 species total",
        metricValue: `${total.toFixed(1)}%`
      };
    }
  });
  return best;
}

function calcMostSpeciesOneDayAward(catches) {
  const leaders = calcTopSpeciesOneDayLeaders(catches, 1);
  if (!leaders || leaders.length === 0) return null;
  const winner = leaders[0];
  return {
    angler_name: winner.angler_name,
    species: `${winner.speciesCount} species`,
    metricLabel: "Most species over One Day weights",
    metricValue: `${winner.speciesCount}`,
    totalWeight: winner.totalWeight
  };
}

function calcTopSpeciesOneDayLeaders(catches, limit = 5) {
  const byAngler = {};
  catches.forEach((c) => {
    if (!c.angler_name || !c.species || c.weight_kg == null) return;
    const species = getCanonicalSpeciesName(c.species);
    const pct = calcPct(species, c.location, c.weight_kg, "od");
    if (!pct || pct < 100) return;
    const key = normalizeName(c.angler_name);
    if (!byAngler[key]) {
      byAngler[key] = {
        angler_name: c.angler_name,
        speciesMap: new Map(),
        totalWeight: 0,
        locations: new Set(),
      };
    }
    const entry = byAngler[key];
    const existing = entry.speciesMap.get(species);
    if (!existing || c.weight_kg > existing.weight_kg) {
      if (existing) {
        entry.totalWeight -= existing.weight_kg;
      }
      entry.speciesMap.set(species, { weight_kg: c.weight_kg, location: c.location });
      entry.totalWeight += c.weight_kg;
    }
    entry.locations.add(c.location);
  });

  return Object.values(byAngler)
    .map((entry) => ({
      angler_name: entry.angler_name,
      speciesCount: entry.speciesMap.size,
      totalWeight: entry.totalWeight,
      locations: Array.from(entry.locations).sort(),
      species: Array.from(entry.speciesMap.entries()).map(([species, info]) => ({
        species,
        weight_kg: info.weight_kg,
        location: info.location,
      })),
    }))
    .sort((a, b) => b.speciesCount - a.speciesCount || b.totalWeight - a.totalWeight)
    .slice(0, limit);
}

function combinations(arr, k) {
  if (k === 0) return [[]];
  if (arr.length < k) return [];
  const [first, ...rest] = arr;
  return [
    ...combinations(rest, k - 1).map(c => [first, ...c]),
    ...combinations(rest, k)
  ];
}

function calcTop5Award(catches, location) {
  const byAngler = {};
  catches.forEach(c => {
    const species = getCanonicalSpeciesName(c.species);
    const pct = calcPct(species, location, c.weight_kg, "s", 150);
    if (!pct || pct <= 0) return;
    const key = normalizeName(c.angler_name);
    if (!byAngler[key]) byAngler[key] = { displayName: c.angler_name, species: {} };
    if (!byAngler[key].species[species] || pct > byAngler[key].species[species].pct) {
      byAngler[key].species[species] = { pct, weight_kg: c.weight_kg };
    }
  });
  const results = [];
  Object.values(byAngler).forEach(({ displayName, species: speciesMap }) => {
    const sorted = Object.entries(speciesMap)
      .map(([species, { pct, weight_kg }]) => ({ species, pct, weight_kg, location }))
      .sort((a, b) => b.pct - a.pct);
    const top5 = sorted.slice(0, 5);
    const total = top5.reduce((sum, s) => sum + s.pct, 0);
    results.push({ angler_name: displayName, total, species: top5 });
  });
  return results.sort((a, b) => b.total - a.total).slice(0, 5);
}

function calcTop5CombinedAward(shoreCatches, boatCatches, award) {
  const byAngler = {};
  const addCatches = (catches, loc) => {
    catches.forEach(c => {
      if (award.excludesJunior && c.is_junior) return;
      const species = getCanonicalSpeciesName(c.species);
      const pct = calcPct(species, loc, c.weight_kg, "s", 150);
      if (!pct || pct <= 0) return;
      const key = normalizeName(c.angler_name);
      if (!byAngler[key]) byAngler[key] = { displayName: c.angler_name, shore: {}, boat: {} };
      if (!byAngler[key][loc][species] || pct > byAngler[key][loc][species].pct) {
        byAngler[key][loc][species] = { pct, weight_kg: c.weight_kg };
      }
    });
  };
  addCatches(shoreCatches, "shore");
  addCatches(boatCatches, "boat");

  const results = [];
  Object.values(byAngler).forEach(({ displayName, shore, boat }) => {
    const shoreList = Object.entries(shore)
      .map(([species, { pct, weight_kg }]) => ({ species, pct, weight_kg, location: "shore" }))
      .sort((a, b) => b.pct - a.pct)
      .slice(0, 8);
    const boatList = Object.entries(boat)
      .map(([species, { pct, weight_kg }]) => ({ species, pct, weight_kg, location: "boat" }))
      .sort((a, b) => b.pct - a.pct)
      .slice(0, 8);

    let best = null;
    [[2, 3], [3, 2]].forEach(([nShore, nBoat]) => {
      if (shoreList.length < nShore || boatList.length < nBoat) return;
      combinations(shoreList, nShore).forEach(sc => {
        const shoreSpecies = new Set(sc.map(s => s.species));
        combinations(boatList, nBoat).forEach(bc => {
          if (bc.some(s => shoreSpecies.has(s.species))) return;
          const all = [...sc, ...bc];
          const total = all.reduce((sum, s) => sum + s.pct, 0);
          if (!best || total > best.total) {
            best = { angler_name: displayName, total, species: all.sort((a, b) => b.pct - a.pct) };
          }
        });
      });
    });

    if (best) results.push(best);
  });
  return results.sort((a, b) => b.total - a.total).slice(0, 5);
}

export default function AwardsPage() {
  const [boatCatches, setBoatCatches] = useState([]);
  const [shoreCatches, setShoreCatches] = useState([]);
  const [yearToDateWinners, setYearToDateWinners] = useState({});
  const [championWinners, setChampionWinners] = useState({});
  const [topSpeciesOneDayLeaders, setTopSpeciesOneDayLeaders] = useState([]);
  const [rayBassLeaders, setRayBassLeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const currentPeriod = getMonthYearInfo(selectedMonth, selectedYear);

  const navigateMonth = (direction) => {
    let newMonth = selectedMonth;
    let newYear = selectedYear;

    if (direction === 'prev') {
      newMonth = selectedMonth - 1;
      if (newMonth < 0) {
        newMonth = 11;
        newYear = selectedYear - 1;
      }
    } else if (direction === 'next') {
      newMonth = selectedMonth + 1;
      if (newMonth > 11) {
        newMonth = 0;
        newYear = selectedYear + 1;
      }
    }

    // Don't allow navigating to future months
    const now = new Date();
    const targetDate = new Date(newYear, newMonth, 1);
    if (targetDate > now) return;

    setSelectedMonth(newMonth);
    setSelectedYear(newYear);
  };

  const goToCurrentMonth = () => {
    const now = new Date();
    setSelectedMonth(now.getMonth());
    setSelectedYear(now.getFullYear());
  };

  useEffect(() => {
    const fetchMonthlyCatches = async () => {
      try {
        setLoading(true);

        // Calculate start and end of selected month
        const startOfMonth = new Date(selectedYear, selectedMonth, 1);
        const endOfMonth = new Date(selectedYear, selectedMonth + 1, 1);

        const startDate = toLocalDateString(startOfMonth);
        const endDate = toLocalDateString(endOfMonth);

        // Get selected month catches for boat (fetch more to sort by percentage)
        const boatResponse = await supabase
          .from("catches")
          .select("id, angler_name, location, species, weight_kg, weigh_date")
          .eq("location", "boat")
          .gte("weigh_date", startDate)
          .lt("weigh_date", endDate)
          .order("weigh_date", { ascending: false })
          .limit(50); // Fetch more to ensure we get enough data to sort by percentage

        // Get selected month catches for shore (fetch more to sort by percentage)
        const shoreResponse = await supabase
          .from("catches")
          .select("id, angler_name, location, species, weight_kg, weigh_date")
          .eq("location", "shore")
          .gte("weigh_date", startDate)
          .lt("weigh_date", endDate)
          .order("weigh_date", { ascending: false })
          .limit(50); // Fetch more to ensure we get enough data to sort by percentage

        // Calculate percentages and sort by percentage descending
        const processCatches = (catches) => {
          return catches
            .map(c => ({
              ...c,
              percentage: calcPct(c.species, c.location, c.weight_kg) || 0
            }))
            .sort((a, b) => b.percentage - a.percentage) // Sort by percentage descending
            .slice(0, 10); // Take top 10
        };

        setBoatCatches(processCatches(boatResponse.data ?? []));
        setShoreCatches(processCatches(shoreResponse.data ?? []));

        // Fetch year-to-date data for cup awards
        const startOfYear = new Date(selectedYear, 0, 1);
        const endOfYearOrNow = new Date();
        const yearStartDate = toLocalDateString(startOfYear);
        const yearEndDate = toLocalDateString(endOfYearOrNow);

        const [allShoreResponse, allBoatResponse] = await Promise.all([
          supabase
            .from("catches")
            .select("*")
            .eq("location", "shore")
            .gte("weigh_date", yearStartDate)
            .lte("weigh_date", yearEndDate)
            .order("weight_kg", { ascending: false }),
          supabase
            .from("catches")
            .select("*")
            .eq("location", "boat")
            .gte("weigh_date", yearStartDate)
            .lte("weigh_date", yearEndDate)
            .order("weight_kg", { ascending: false })
        ]);

        const allShoreCatches = allShoreResponse.data ?? [];
        const allBoatCatches = allBoatResponse.data ?? [];
        const winners = {};

        // Process each species award
        SPECIES_AWARDS.forEach((award) => {
          const catches = award.location === "boat" ? allBoatCatches : allShoreCatches;
          // Awards listed earlier in SPECIES_AWARDS are decided first, so their winning catch can be excluded here.
          const excludedCatchIds = (award.excludeWinnersOf || [])
            .map((name) => winners[name]?.id)
            .filter((id) => id != null);
          const eligible = catches.filter((c) => matchesAward(c, award) && !excludedCatchIds.includes(c.id));

          if (eligible.length > 0) {
            // Sort by CFSA % so multi-species "best specimen" awards (e.g. Ron Stevens Cup)
            // pick the highest percentage fish, not just the heaviest.
            // Awards flagged rankByWeight (e.g. Stephens and Viant Cup) go to the heaviest fish instead.
            const winner = eligible
              .map(c => ({ ...c, _pct: calcPct(getCanonicalSpeciesName(c.species), c.location, c.weight_kg) || 0 }))
              .sort((a, b) => award.rankByWeight
                ? (b.weight_kg || 0) - (a.weight_kg || 0)
                : b._pct - a._pct)[0];
            winners[award.name] = {
              ...winner,
              awardName: award.name,
              awardDescription: award.description
            };
          }
        });

        const allCatches = [...allShoreCatches, ...allBoatCatches];
        BOAT_AND_SHORE_TROPHIES.forEach((award) => {
          let winner = null;
          if (award.type === "heaviest") {
            winner = calcHeaviestAward(allCatches, award);
          } else if (award.type === "top3junior") {
            winner = calcTop3JuniorAward(allCatches);
          } else if (award.type === "mostSpeciesOneDay") {
            winner = calcMostSpeciesOneDayAward(allCatches);
          }

          if (winner) {
            winners[award.name] = {
              ...winner,
              awardName: award.name,
              awardDescription: award.description
            };
          }
        });

        setYearToDateWinners(winners);
        setTopSpeciesOneDayLeaders(calcTopSpeciesOneDayLeaders(allCatches, 5));

        // Process champion awards
        const champWinners = {};
        CHAMPION_AWARDS.forEach((award) => {
          let winner = null;
          if (award.type === "top5combined") {
            winner = calcTop5CombinedAward(allShoreCatches, allBoatCatches, award);
          } else if (award.type === "top5") {
            const catches = award.location === "shore" ? allShoreCatches : allBoatCatches;
            winner = calcTop5Award(catches, award.location);
          }
          if (winner && winner.length > 0) champWinners[award.name] = winner;
        });
        setChampionWinners(champWinners);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching catches: - AwardsPage.js:550", error);
        setLoading(false);
      }
    };

    fetchMonthlyCatches();
  }, [selectedMonth, selectedYear]);

  // Ray and Bass Hunt: always October's shore catches, shown until 10th November
  // regardless of the month navigator above.
  useEffect(() => {
    if (!isRayBassHuntActive()) return;

    const huntYear = new Date().getFullYear();
    const { start, end } = huntDateRange(huntYear);

    supabase
      .from("catches")
      .select("*")
      .eq("location", "shore")
      .gte("weigh_date", start)
      .lte("weigh_date", end)
      .then(({ data }) => setRayBassLeaders(buildRayBassLeaderboard(data ?? [], huntYear)));
  }, []);

  const renderCatchList = (catches, title, location) => {
    const ranks = [];
    catches.forEach((c, i) => {
      if (i === 0) { ranks.push(1); return; }
      ranks.push(catches[i - 1].percentage === c.percentage ? ranks[i - 1] : i + 1);
    });
    return (
    <div className="space-y-4">
      <h3 className="text-xl font-bold text-white flex items-center gap-2">
        <Award className="h-5 w-5 text-cyan-400" />
        {title}
      </h3>
      {catches.length === 0 ? (
        <p className="text-slate-400">No catches recorded for {currentPeriod.fullName}</p>
      ) : (
        <div className="grid gap-3">
          {catches.map((c, index) => {
            const pct = c.percentage;
            const rank = ranks[index];
            return (
              <div key={c.id} className="rounded-lg border border-white/10 bg-white/5 p-4 panel-lift">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-cyan-400">#{rank}</span>
                    <span className="font-semibold text-white">{c.angler_name}</span>
                  </div>
                  <span className={`shrink-0 rounded-md px-2 py-0.5 text-xs font-semibold uppercase tracking-wide ${location === "boat" ? "bg-sky-400/20 text-sky-300" : "bg-emerald-400/20 text-emerald-300"}`}>
                    {location}
                  </span>
                </div>
                <p className="text-sm text-slate-300 mb-1">{c.species}</p>
                <p className="text-lg font-bold text-white">{formatKgLbsOz(c.weight_kg)}</p>
                {pct !== null && pct > 0 && (
                  <>
                    <p className={`mt-1 text-sm font-semibold ${pct >= 100 ? "text-yellow-400" : "text-emerald-400"}`}>
                      {pct.toFixed(1)}% of CFSA specimen
                    </p>
                    <div className="mt-2 h-2 w-full rounded-full bg-white/10">
                      <div
                        className={`h-2 rounded-full transition-all ${pct >= 100 ? "bg-yellow-400" : "bg-emerald-400"}`}
                        style={{ width: `${Math.min(pct, 100)}%` }}
                      />
                    </div>
                  </>
                )}
                {c.weigh_date && <p className="mt-2 text-xs text-slate-500">{formatDate(c.weigh_date)}</p>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
  };

  if (loading) {
    return (
      <div className="page-shell min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <PageMeta
          title="Fish of the Month Awards | Redruth Sea Angling Association"
          description="See Redruth Sea Angling Association's Fish of the Month awards and competition results, celebrating the best shore and boat catches from RSAA club anglers in Cornwall."
          keywords="fish of the month, RSAA awards, sea angling competition results Cornwall, Redruth Sea Angling Association awards"
          url="https://www.redruthseaanglingassociation.co.uk/awards"
        />
        <div className="mx-auto max-w-7xl">
          <div className="flex items-center gap-3 mb-8">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-cyan-400/10 text-cyan-300">
              <Trophy className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.3em] text-cyan-300">Club Awards</p>
              <h1 className="heading-glow text-3xl font-bold tracking-tight text-white">Fish of the Month Awards</h1>
            </div>
          </div>
          <p className="text-slate-400">Loading awards data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-shell min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
      <PageMeta
        title="Fish of the Month Awards | Redruth Sea Angling Association"
        description="See Redruth Sea Angling Association's Fish of the Month awards and competition results, celebrating the best shore and boat catches from RSAA club anglers in Cornwall."
        keywords="fish of the month, RSAA awards, sea angling competition results Cornwall, Redruth Sea Angling Association awards"
        url="https://www.redruthseaanglingassociation.co.uk/awards"
      />
      <div className="mx-auto max-w-7xl">
        <div className="standalone-only mb-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-lg border border-cyan-300/40 bg-cyan-400/10 px-4 py-2 text-sm text-cyan-200 transition hover:bg-cyan-400/20"
          >
            ← Back
          </Link>
        </div>

        {rayBassLeaders.length > 0 && (
          <div className="mb-10 rounded-2xl border border-emerald-400/20 bg-emerald-400/5 p-8 panel-lift">
            <div className="flex items-center gap-3 mb-8">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-400/10 text-emerald-300">
                <Trophy className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Ray and Bass Hunt</h2>
                <p className="text-slate-400">
                  Best combined percentage of one ray (any species) and one bass, Shore only, October {new Date().getFullYear()}.
                  Anglers with both a ray and a bass rank above those still waiting on one.
                </p>
              </div>
            </div>
            <RayBassLeaderboard entries={rayBassLeaders} />
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3 mb-8">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-cyan-400/10 text-cyan-300">
            <Trophy className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-[16rem]">
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-cyan-300">Club Awards</p>
            <h1 className="heading-glow text-3xl font-bold tracking-tight text-white">Fish of the Month Awards</h1>
            <p className="mt-2 text-slate-400">Best specimen catches for {currentPeriod.fullName}</p>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <button
              onClick={() => navigateMonth('prev')}
              className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/5 text-slate-400 hover:bg-white/10 hover:text-white transition-colors"
              title="Previous month"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              onClick={goToCurrentMonth}
              className="px-3 py-1 text-sm rounded-lg bg-cyan-400/10 text-cyan-300 hover:bg-cyan-400/20 transition-colors"
              title="Go to current month"
            >
              Current
            </button>
            <button
              onClick={() => navigateMonth('next')}
              className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${
                new Date(selectedYear, selectedMonth + 1, 1) > new Date()
                  ? 'bg-white/5 text-slate-600 cursor-not-allowed'
                  : 'bg-white/5 text-slate-400 hover:bg-white/10 hover:text-white'
              }`}
              disabled={new Date(selectedYear, selectedMonth + 1, 1) > new Date()}
              title="Next month"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="grid gap-8 lg:grid-cols-2">
          {renderCatchList(boatCatches, `Boat - ${currentPeriod.fullName}`, "boat")}
          {renderCatchList(shoreCatches, `Shore - ${currentPeriod.fullName}`, "shore")}
        </div>

        <div className="mt-10 rounded-2xl border border-slate-700 bg-slate-900/80 p-6 shadow-xl shadow-slate-950/30">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.3em] text-cyan-300">Species Hunt</p>
              <p className="mt-1 text-white text-lg font-semibold">See who is winning the Species Hunt Race {selectedYear}</p>
            </div>
            <Link
              to="/species-hunt"
              className="inline-flex items-center justify-center rounded-full border border-cyan-300/30 bg-cyan-400/10 px-5 py-3 text-sm font-semibold text-cyan-300 transition hover:bg-cyan-400/20 hover:text-white"
            >
              Go to Species Hunt
            </Link>
          </div>
        </div>

        {/* Shore Trophies */}
        <div className="mt-16 rounded-2xl border border-white/10 bg-white/5 p-8 panel-lift">
          <div className="flex items-center gap-3 mb-8">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-400/10 text-amber-300">
              <Trophy className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Shore Trophies</h2>
              <p className="text-slate-400">Year-to-date winners for {selectedYear}</p>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {SPECIES_AWARDS.filter(a => !a.requiresLadies && !a.requiresJunior && a.location === "shore").map((award) => {
              const winner = yearToDateWinners[award.name];
              return (
                <div key={award.name} className="rounded-lg border border-white/10 bg-white/5 p-4 hover:bg-white/10 transition panel-lift">
                  <h3 className="font-semibold text-white mb-1">{award.name}</h3>
                  <p className="text-sm text-slate-400 mb-3">{award.description}</p>
                  {winner ? (
                    <div className="space-y-2">
                      <p className="text-sm font-bold text-amber-300">{winner.angler_name}</p>
                      <p className="text-sm text-slate-300">{winner.species}</p>
                      <p className="text-lg font-bold text-white">{formatKgLbsOz(winner.weight_kg)}</p>
                      {winner.weigh_date && <p className="text-xs text-slate-500">{formatDate(winner.weigh_date)}</p>}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500 italic">No winner recorded yet</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Boat & Shore Trophies */}
        <div className="mt-10 rounded-2xl border border-cyan-400/20 bg-cyan-400/5 p-8 panel-lift">
          <div className="flex items-center gap-3 mb-8">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-cyan-400/10 text-cyan-300">
              <Trophy className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Boat & Shore Trophies</h2>
              <p className="text-slate-400">Year-to-date winners for {selectedYear}</p>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {BOAT_AND_SHORE_TROPHIES.filter(a => !a.requiresJunior).map((award) => {
              const winner = yearToDateWinners[award.name];
              return (
                <div key={award.name} className="rounded-lg border border-white/10 bg-white/5 p-4 hover:bg-white/10 transition panel-lift">
                  <h3 className="font-semibold text-white mb-1">{award.name}</h3>
                  <p className="text-sm text-slate-400 mb-3">{award.description}</p>
                  {winner ? (
                    <div className="space-y-2">
                      <p className="text-sm font-bold text-cyan-300">{winner.angler_name}</p>
                      <p className="text-sm text-slate-300">{winner.species}</p>
                      {winner.metricLabel ? (
                        <p className="text-lg font-bold text-white">{winner.metricValue}</p>
                      ) : winner.weight_kg != null ? (
                        <p className="text-lg font-bold text-white">{formatKgLbsOz(winner.weight_kg)}</p>
                      ) : null}
                      {winner.weigh_date && <p className="text-xs text-slate-500">{formatDate(winner.weigh_date)}</p>}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500 italic">No winner recorded yet</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Junior Cups & Trophies */}
        <div className="mt-10 rounded-2xl border border-violet-400/20 bg-violet-400/5 p-8 panel-lift">
          <div className="flex items-center gap-3 mb-8">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-400/10 text-violet-300">
              <Trophy className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Junior Cups & Trophies</h2>
              <p className="text-slate-400">Year-to-date winners for {selectedYear}</p>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {JUNIOR_TROPHIES.map((award) => {
              const winner = yearToDateWinners[award.name];
              return (
                <div key={award.name} className="rounded-lg border border-white/10 bg-white/5 p-4 hover:bg-white/10 transition panel-lift">
                  <h3 className="font-semibold text-white mb-1">{award.name}</h3>
                  <p className="text-sm text-slate-400 mb-3">{award.description}</p>
                  {winner ? (
                    <div className="space-y-2">
                      <p className="text-sm font-bold text-violet-300">{winner.angler_name}</p>
                      <p className="text-sm text-slate-300">{winner.species}</p>
                      {winner.metricLabel ? (
                        <p className="text-lg font-bold text-white">{winner.metricValue}</p>
                      ) : winner.weight_kg != null ? (
                        <p className="text-lg font-bold text-white">{formatKgLbsOz(winner.weight_kg)}</p>
                      ) : null}
                      {winner.weigh_date && <p className="text-xs text-slate-500">{formatDate(winner.weigh_date)}</p>}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500 italic">No winner recorded yet</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* One Day Species Leaders */}
        <div className="mt-10 rounded-2xl border border-white/10 bg-white/5 p-8 panel-lift">
          <div className="flex items-center gap-3 mb-8">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-cyan-400/10 text-cyan-300">
              <Award className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Sandy Underwood One Day Leaders</h2>
              <p className="text-slate-400">Top 5 anglers with the most species over One Day weights this year (Boat and Shore).</p>
            </div>
          </div>
          {topSpeciesOneDayLeaders.length > 0 ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {topSpeciesOneDayLeaders.map((entry, index) => (
                <div key={entry.angler_name} className="rounded-lg border border-white/10 bg-white/5 p-4 hover:bg-white/10 transition panel-lift">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-sm text-slate-400">#{index + 1}</p>
                      <p className="text-lg font-semibold text-white">{entry.angler_name}</p>
                    </div>
                    <span className="text-xs uppercase tracking-[0.2em] text-slate-500">{entry.locations.join("/")}</span>
                  </div>
                  <p className="text-sm text-slate-300">{entry.speciesCount} species over One Day weights</p>
                  <div className="mt-4 space-y-2">
                    {entry.species.map((speciesEntry) => (
                      <div key={speciesEntry.species} className="flex items-center justify-between rounded-lg border border-white/10 bg-slate-950/50 px-3 py-2 text-sm text-slate-200">
                        <span>{speciesEntry.species}</span>
                        <span className="font-semibold text-white">{formatKgLbsOz(speciesEntry.weight_kg)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                    <span className="font-bold text-white">{entry.speciesCount}</span>
                    <span className="text-xs text-slate-400">{entry.locations.join("/")}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-400">No one-day qualifying species recorded yet.</p>
          )}
        </div>

        {/* Boat Awards */}
        <div className="mt-10 rounded-2xl border border-sky-400/20 bg-sky-400/5 p-8">
          <div className="flex items-center gap-3 mb-8">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-sky-400/10 text-sky-300">
              <Trophy className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Boat Awards</h2>
              <p className="text-slate-400">Year-to-date winners for {selectedYear}</p>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {SPECIES_AWARDS.filter(a => a.location === "boat" && !a.requiresJunior).map((award) => {
              const winner = yearToDateWinners[award.name];
              return (
                <div key={award.name} className="rounded-lg border border-white/10 bg-white/5 p-4 hover:bg-white/10 transition panel-lift">
                  <h3 className="font-semibold text-white mb-1">{award.name}</h3>
                  <p className="text-sm text-slate-400 mb-3">{award.description}</p>
                  {winner ? (
                    <div className="space-y-2">
                      <p className="text-sm font-bold text-sky-300">{winner.angler_name}</p>
                      <p className="text-sm text-slate-300">{winner.species}</p>
                      <p className="text-lg font-bold text-white">{formatKgLbsOz(winner.weight_kg)}</p>
                      {winner.weigh_date && <p className="text-xs text-slate-500">{formatDate(winner.weigh_date)}</p>}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500 italic">No winner recorded yet</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Ladies Shore Awards */}
        <div className="mt-10 rounded-2xl border border-pink-400/20 bg-pink-400/5 p-8">
          <div className="flex items-center gap-3 mb-8">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-pink-400/10 text-pink-300">
              <Trophy className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Ladies Shore Awards</h2>
              <p className="text-slate-400">Year-to-date winners for {selectedYear}</p>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {SPECIES_AWARDS.filter(a => a.requiresLadies).map((award) => {
              const winner = yearToDateWinners[award.name];
              return (
                <div key={award.name} className="rounded-lg border border-white/10 bg-white/5 p-4 hover:bg-white/10 transition panel-lift">
                  <h3 className="font-semibold text-white mb-1">{award.name}</h3>
                  <p className="text-sm text-slate-400 mb-3">{award.description}</p>
                  {winner ? (
                    <div className="space-y-2">
                      <p className="text-sm font-bold text-pink-300">{winner.angler_name}</p>
                      <p className="text-sm text-slate-300">{winner.species}</p>
                      <p className="text-lg font-bold text-white">{formatKgLbsOz(winner.weight_kg)}</p>
                      {winner.weigh_date && <p className="text-xs text-slate-500">{formatDate(winner.weigh_date)}</p>}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500 italic">No winner recorded yet</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Champion Awards */}
        <div className="mt-10 rounded-2xl border border-amber-400/20 bg-amber-400/5 p-8 panel-lift">
          <div className="flex items-center gap-3 mb-8">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-400/20 text-amber-300">
              <Trophy className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Champion Awards</h2>
              <p className="text-slate-400">Best total CFSA Specimen percentage across 5 species – {selectedYear}</p>
            </div>
          </div>
          <div className="space-y-10">
            {CHAMPION_AWARDS.map((award) => {
              const leaders = championWinners[award.name];
              return (
                <div key={award.name}>
                  <div className="flex items-center gap-2 mb-4">
                    <Trophy className="h-4 w-4 text-amber-400 shrink-0" />
                    <div>
                      <h3 className="font-bold text-white text-lg">{award.name}</h3>
                      <p className="text-xs text-slate-400">{award.description}</p>
                    </div>
                  </div>
                  {leaders && leaders.length > 0 ? (
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {leaders.map((entry, idx) => (
                        <div key={idx} className="rounded-lg border border-white/10 bg-white/5 p-4 hover:bg-white/10 transition panel-lift">
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <p className="text-sm text-slate-400">#{idx + 1}</p>
                              <p className="text-lg font-semibold text-amber-300">{entry.angler_name}</p>
                            </div>
                            <span className="text-xs text-slate-400">Total: <span className="text-white font-semibold">{entry.total.toFixed(1)}%</span></span>
                          </div>
                          <p className="text-sm text-slate-400 mb-3">{entry.species.length} species</p>
                          <div className="space-y-2">
                            {entry.species.map((s, i) => (
                              <div key={i} className="rounded-lg border border-white/10 bg-slate-950/50 px-3 py-2 text-sm">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2 min-w-0">
                                    <span className="text-slate-200 truncate">{s.species}</span>
                                    {award.type === "top5combined" && (
                                      <span className={`shrink-0 text-xs px-1.5 py-0.5 rounded ${s.location === "boat" ? "bg-sky-400/20 text-sky-300" : "bg-emerald-400/20 text-emerald-300"}`}>
                                        {s.location}
                                      </span>
                                    )}
                                  </div>
                                  <span className={`shrink-0 ml-2 font-semibold ${s.pct >= 100 ? "text-yellow-400" : "text-emerald-400"}`}>
                                    {s.pct.toFixed(1)}%
                                  </span>
                                </div>
                                {s.weight_kg != null && (
                                  <p className="mt-0.5 text-xs text-slate-400">{formatKgLbsOz(s.weight_kg)}</p>
                                )}
                              </div>
                            ))}
                          </div>
                          <div className="mt-3 flex items-center justify-between">
                            <span className="font-bold text-white">{entry.species.length}</span>
                            <span className="text-xs text-slate-500">{entry.total.toFixed(1)}%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500 italic">No winner recorded yet</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}